import { addDays, partsOf } from '@shared/date/civil'
import type { CivilDate } from '@shared/date/civil'
import type { Transaction } from '@domain/finance'
import { MONTH_NAMES } from './labels'

/**
 * A lista agrupada por dia, como `Docs/04-Design/Telas/Transações.md` desenha:
 * *"cabeçalho de grupo com data relativa e data completa (`Hoje · 15 de
 * Julho`)"*, em ordem decrescente.
 *
 * ## Puro, e por isso testável sem tela
 *
 * `hoje` entra por parâmetro — a única leitura de relógio do app mora em
 * `shared/date/timezone.ts` (decisão 79), e um agrupador que a chamasse teria
 * de ser testado às voltas do fuso. Aqui "Hoje" é uma comparação de duas
 * strings.
 *
 * ## Sem `Intl.DateTimeFormat`
 *
 * O lint o proíbe fora de `shared/date/`, porque sem `timeZone` explícito ele
 * formata no fuso de quem roda. E aqui não há instante nenhum a converter:
 * `CivilDate` **já é** o dia. A tabela de meses de `labels.ts` responde, pelo
 * mesmo motivo que ela existe para o seletor — e é a **mesma** tabela, não uma
 * cópia: doze nomes em português, com "março" acentuado, são exatamente o tipo
 * de lista que alguém recopia errado.
 */

export type DayGroup = {
  readonly date: CivilDate
  /** `'Hoje'`, `'Ontem'` ou `null` quando o dia não tem nome próprio. */
  readonly relative: string | null
  /** `'15 de julho'` — sempre presente, mesmo quando há o relativo. */
  readonly full: string
  readonly transactions: Transaction[]
}

/**
 * `'15 de julho'`. Sem o ano de propósito: a tela já está num mês selecionado,
 * e repetir o ano em cada cabeçalho é ruído. O ano volta quando a fase 4
 * trouxer o intervalo livre, que é quando ele passa a distinguir alguma coisa.
 */
export function fullDayLabel(date: CivilDate): string {
  const { month, day } = partsOf(date)
  return `${String(day)} de ${MONTH_NAMES[month - 1] ?? ''}`
}

/** `'Hoje'`, `'Ontem'`, ou `null`. Comparação de string, sem relógio. */
export function relativeDayLabel(date: CivilDate, today: CivilDate): string | null {
  if (date === today) return 'Hoje'
  if (date === addDays(today, -1)) return 'Ontem'
  // **Não há "Amanhã".** Parcela futura existe e é normal (a compra de 12x tem
  // lançamento até um ano à frente), mas chamar de "amanhã" uma transação que
  // ainda não aconteceu daria a ela uma familiaridade que ela não tem.
  return null
}

/**
 * Agrupa por `source.transactionDate`, do mais recente para o mais antigo.
 *
 * **A data é a de competência, não a de lançamento**, porque é ela que decide
 * em que mês a transação aparece — e um agrupamento por outra data mostraria
 * dias fora do mês selecionado.
 */
export function groupByDay(
  transactions: readonly Transaction[],
  today: CivilDate,
): DayGroup[] {
  const porDia = new Map<CivilDate, Transaction[]>()
  for (const t of transactions) {
    const dia = t.source.transactionDate
    porDia.set(dia, [...(porDia.get(dia) ?? []), t])
  }

  return [...porDia.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([date, ts]) => ({
      date,
      relative: relativeDayLabel(date, today),
      full: fullDayLabel(date),
      // Dentro do dia, o maior valor absoluto primeiro: é o que a pessoa
      // procura quando abre um dia cheio, e a ordem de chegada do Firestore
      // não significa nada para quem lê.
      transactions: [...ts].sort(
        (x, y) => Math.abs(y.source.amountCents) - Math.abs(x.source.amountCents),
      ),
    }))
}

/**
 * Os dias que ainda não chegaram, separados dos outros — para a lista os
 * guardar numa dobra fechada.
 *
 * ## Por que dobrar
 *
 * Parcela futura é normal: a compra de 3x lança a terceira parcela com a data
 * dela, semanas à frente. Ordenada do mais recente para o mais antigo, ela ia
 * para o TOPO da lista, e a primeira coisa que o mês mostrava era uma compra
 * que ainda não aconteceu ("28 de setembro · Sh Muller 2/3" num dia 13).
 *
 * ## Hoje não é futuro
 *
 * `date > today`, estrito: o que caiu hoje é a coisa mais recente do mês, e é
 * exatamente o que deve ficar no topo.
 *
 * ## O mês inteiro no futuro não dobra
 *
 * Abrir um mês que ainda não começou mostraria só uma dobra fechada, e a tela
 * pareceria vazia. Quando não sobra nada fora dela, não há o que separar.
 */
export function separarFuturos(
  groups: readonly DayGroup[],
  today: CivilDate,
): { futuros: DayGroup[]; resto: DayGroup[] } {
  const futuros = groups.filter((g) => g.date > today)
  const resto = groups.filter((g) => g.date <= today)
  if (resto.length === 0) return { futuros: [], resto: [...groups] }
  return { futuros, resto }
}

/** "1 transação futura", "3 transações futuras" — o título da dobra. */
export function rotuloDasFuturas(futuros: readonly DayGroup[]): string {
  const n = futuros.reduce((soma, g) => soma + g.transactions.length, 0)
  return n === 1 ? '1 transação futura' : `${String(n)} transações futuras`
}
